/**
 * useMailshot Hook
 * 
 * React hook for mailshot operations using MailshotRepository.
 * Provides loading states, error handling, and real-time updates.
 * 
 * Usage:
 * - Single mailshot: useMailshot(mailshotId)
 * - Operations only: useMailshot()
 */

import { useState, useEffect, useCallback } from 'react';
import { MailshotRepository } from '../models/MailshotRepository';
import { Mailshot } from '../models/Mailshot';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';

const repo = new MailshotRepository(db, functions);

export const useMailshot = (mailshotId = null) => {
  const [mailshot, setMailshot] = useState(null);
  const [loading, setLoading] = useState(!!mailshotId);
  const [error, setError] = useState(null);

  // Fetch single mailshot with real-time updates
  useEffect(() => {
    if (!mailshotId) return;

    setLoading(true);
    setError(null);

    // Timeout guard: if onSnapshot never fires (stale Firestore channel), stop spinning after 15s
    const timeoutId = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('🔧 [NEW ARCH] useMailshot: onSnapshot timed out for', mailshotId);
          setError(new Error('Firestore connection timed out. Hard-refresh the page (Cmd+Shift+R) to fix.'));
          return false;
        }
        return current;
      });
    }, 15000);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'discovery_mailshots', mailshotId),
      async (docSnapshot) => {
        clearTimeout(timeoutId);
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          
          // If promoters data is in subcollection, load it
          if (data._promotersDataPath) {
            try {
              // Use proper web SDK syntax for subcollection access
              const promotersDataRef = doc(db, 'discovery_mailshots', mailshotId, 'promoters_data', 'main');
              const promotersDataDoc = await getDoc(promotersDataRef);
              if (promotersDataDoc.exists()) {
                const promotersData = promotersDataDoc.data();
                // Merge subcollection data into main document data
                data.promotersInArea = promotersData.promotersInArea;
                data.promoterPropertyMatches = promotersData.promoterPropertyMatches;
              } else {
                console.warn('⚠️ Subcollection document does not exist:', data._promotersDataPath);
              }
            } catch (err) {
              console.error('❌ Error loading promoters data from subcollection:', err);
            }
          }
          
          // Convert to Mailshot instance for business logic methods
          const mailshotInstance = new Mailshot({ id: docSnapshot.id, ...data });
          setMailshot(mailshotInstance);
          setError(null);
        } else {
          setMailshot(null);
          setError(new Error('Mailshot not found'));
        }
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('Error fetching mailshot:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [mailshotId]);

  // Delete mailshot (cascade to properties/campaign via Cloud Function)
  const deleteMailshot = useCallback(async () => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.delete(mailshotId);
    } catch (err) {
      console.error('Error deleting mailshot:', err);
      throw err;
    }
  }, [mailshotId]);

  // Recalculate cached counts (rebuild from actual property data via Cloud Function)
  const recalculateCounts = useCallback(async () => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.recalculateCounts(mailshotId);
    } catch (err) {
      console.error('Error recalculating counts:', err);
      throw err;
    }
  }, [mailshotId]);

  // Add properties to mailshot (atomic operation via Cloud Function)
  const addProperties = useCallback(async (propertyIds) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.addProperties(mailshotId, propertyIds);
    } catch (err) {
      console.error('Error adding properties:', err);
      throw err;
    }
  }, [mailshotId]);

  // Remove properties from mailshot (atomic operation via Cloud Function)
  const removeProperties = useCallback(async (propertyIds) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.removeProperties(mailshotId, propertyIds);
    } catch (err) {
      console.error('Error removing properties:', err);
      throw err;
    }
  }, [mailshotId]);

  // Prune mailshot by criteria (e.g., remove all 'Sold STC' properties via Cloud Function)
  const prune = useCallback(async (criteria) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.prune(mailshotId, criteria);
    } catch (err) {
      console.error('Error pruning mailshot:', err);
      throw err;
    }
  }, [mailshotId]);

  // Add scrape to mailshot (atomic operation via Cloud Function)
  const addScrape = useCallback(async (scrapeId) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.addScrape(mailshotId, scrapeId);
    } catch (err) {
      console.error('Error adding scrape:', err);
      throw err;
    }
  }, [mailshotId]);

  // Remove scrape from mailshot (atomic operation via Cloud Function)
  const removeScrape = useCallback(async (scrapeId) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.removeScrape(mailshotId, scrapeId);
    } catch (err) {
      console.error('Error removing scrape:', err);
      throw err;
    }
  }, [mailshotId]);

  // Update mailshot fields
  const update = useCallback(async (updates) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    
    try {
      await repo.update(mailshotId, updates);
    } catch (err) {
      console.error('Error updating mailshot:', err);
      throw err;
    }
  }, [mailshotId]);

  // The go link printed on the mailshot: through the function, which checks it (Answers { link, redirects }).
  const setPrintedGoLink = useCallback(async (link) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');
    return repo.setPrintedGoLink(mailshotId, link);
  }, [mailshotId]);

  // Force refresh (mainly for testing)
  const refresh = useCallback(() => {
    if (!mailshotId) return;
    setLoading(true);
    // onSnapshot will automatically trigger with latest data
  }, [mailshotId]);

  /**
   * Mark mailshot as mailed out with timestamp
   * Updates all properties with sentHistory and sets deliveryStatus to 'sent'
   */
  const markAsMailed = useCallback(async (mailedDate = new Date()) => {
    try {
      return await repo.markAsMailed(mailshotId, mailedDate);
    } catch (err) {
      console.error('Error marking mailshot as mailed:', err);
      throw err;
    }
  }, [mailshotId, repo]);

  /**
   * Unmark mailshot as mailed out
   * Restores original deliveryStatus on all properties
   */
  const unmarkAsMailed = useCallback(async () => {
    try {
      await repo.unmarkAsMailed(mailshotId);
    } catch (err) {
      console.error('Error unmarking mailshot as mailed:', err);
      throw err;
    }
  }, [mailshotId, repo]);

  /**
   * Update delivery status for multiple properties
   */
  const updateDeliveryStatus = useCallback(async (propertyIds, deliveryStatus) => {
    try {
      await repo.updateDeliveryStatus(propertyIds, deliveryStatus);
    } catch (err) {
      console.error('Error updating delivery status:', err);
      throw err;
    }
  }, [mailshotId]);

  /**
   * Calculate promoters within mailshot area
   * Finds all promoters within search circles + buffer, calculates
   * nearby property counts and nearest property distances.
   *
   * @param {number} catchmentBuffer - Buffer in miles beyond search radius (default 0.5)
   * @returns {Promise<{success: boolean, promotersCount: number, warnings: string[]}>}
   */
  const calculatePromoters = useCallback(async (catchmentBuffer = 1, nearbyRadius = 1, matchingThreshold = 0.70) => {
    if (!mailshotId) throw new Error('No mailshot ID provided');

    try {
      const result = await repo.calculatePromoters(mailshotId, catchmentBuffer, nearbyRadius, matchingThreshold);
      return result;
    } catch (err) {
      console.error('Error calculating promoters:', err);
      throw err;
    }
  }, [mailshotId]);

  return {
    mailshot,
    loading,
    error,
    deleteMailshot,
    recalculateCounts,
    addProperties,
    removeProperties,
    prune,
    addScrape,
    removeScrape,
    update,
    setPrintedGoLink,
    markAsMailed,
    unmarkAsMailed,
    updateDeliveryStatus,
    calculatePromoters,
    refresh
  };
};
